import { useEffect, useLayoutEffect, useRef } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import Home from "@/pages/Home";
import PlannerPage from "@/pages/PlannerPage";
import PlanPage from "@/pages/PlanPage";
import AreasPage from "@/pages/AreasPage";
import AreaDetail from "@/pages/AreaDetail";
import TripDetail from "@/pages/TripDetail";
import TripsPage from "@/pages/TripsPage";
import BeyondCopenhagenPage from "@/pages/BeyondCopenhagenPage";
import MapPage from "@/pages/MapPage";
import AccommodationPage from "@/pages/AccommodationPage";
import BakeriesPage from "@/pages/BakeriesPage";
import TransportPage from "@/pages/TransportPage";
import BikingPage from "@/pages/BikingPage";
import ChristmasPage from "@/pages/ChristmasPage";
import ChristmasWalkPage from "@/pages/ChristmasWalkPage";
import BakeryCrawlPage from "@/pages/BakeryCrawlPage";
import ChristmasFoodPage from "@/pages/ChristmasFoodPage";
import GloggPage from "@/pages/GloggPage";
import BudgetPage from "@/pages/BudgetPage";
import RainyDayPage from "@/pages/RainyDayPage";
import LayoverPage from "@/pages/LayoverPage";
import HarbourSwimmingPage from "@/pages/HarbourSwimmingPage";
import SmorrebrodPage from "@/pages/SmorrebrodPage";
import DanishFoodPage from "@/pages/DanishFoodPage";
import HcAndersenPage from "@/pages/HcAndersenPage";
import PhotoStreetsPage from "@/pages/PhotoStreetsPage";
import ShoppingStreetsPage from "@/pages/ShoppingStreetsPage";
import FoodMarketsPage from "@/pages/FoodMarketsPage";
import ThingsToDoPage from "@/pages/ThingsToDoPage";
import ItineraryPage from "@/pages/ItineraryPage";
import EdtPage from "@/pages/EdtPage";
import MonthPage from "@/pages/MonthPage";
import { MONTH_SLUGS } from "@/data/months";
import KidsPage from "@/pages/KidsPage";
import LikeALocalPage from "@/pages/LikeALocalPage";
import KidsItineraryPage from "@/pages/KidsItineraryPage";
import WinterPage from "@/pages/WinterPage";
import AdvertisePage from "@/pages/AdvertisePage";
import AdvertiseSuccess from "@/pages/AdvertiseSuccess";
import AdvertiseCancel from "@/pages/AdvertiseCancel";
import TermsPage from "@/pages/TermsPage";
import AdminPage from "@/pages/AdminPage";
import { trackPageview } from "@/lib/analytics";
import { loadActiveAds } from "@/lib/ads";

function NotFound() {
  return (
    <main data-testid="not-found" className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 bg-[#F9F8F6]">
      <Seo
        title="Page not found · CPH Highlights"
        description="That page wandered off. The Copenhagen guide is one click away."
        path="/404"
        noindex
      />
      <p className="font-display font-900 tracking-tighter text-7xl md:text-8xl">404<span className="text-[#DF3226]">.</span></p>
      <p className="font-body text-base md:text-lg text-black/60 mt-4 max-w-md">
        This page wandered off to Nyhavn. The rest of the guide is right where you left it.
      </p>
      <Link
        to="/"
        data-testid="not-found-home"
        className="mt-8 inline-flex items-center gap-2 font-display font-900 text-lg bg-black text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
      >
        Back to start
      </Link>
    </main>
  );
}

function Layout({ children }) {  const location = useLocation();
  const navigate = useNavigate();
  const lenisRef = useRef(null);
  const pendingHash = useRef(null);

  useEffect(() => {
    // fetch active advertiser listings once (also registers tier 2+ ads into the planner)
    loadActiveAds();
  }, []);

  useEffect(() => {
    // deter casual copying of text/images: block the right-click context menu
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    return () => document.removeEventListener("contextmenu", block);
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
      window.__lenis = null;
    };
  }, []);

  // Remove the static fallback SEO tags (marked data-fallback in index.html) once
  // Helmet has rendered the page's own — otherwise prerendered pages carry both.
  // Each fallback is only removed when another tag with the same key exists.
  // Layout effect so it runs before react-snap captures the prerendered HTML.
  useLayoutEffect(() => {
    document.querySelectorAll("[data-fallback]").forEach((el) => {
      if (el.tagName === "TITLE") {
        // Helmet renders its own title — drop the shell's so there's only one
        if (document.querySelectorAll("title").length > 1) el.remove();
        return;
      }
      const sel =
        el.tagName === "LINK"
          ? 'link[rel="canonical"]'
          : el.getAttribute("name")
            ? `meta[name="${el.getAttribute("name")}"]`
            : `meta[property="${el.getAttribute("property")}"]`;
      const twins = [...document.querySelectorAll(sel)];
      if (twins.some((x) => x !== el && !x.hasAttribute("data-fallback"))) el.remove();
    });
  }, []);


  const scrollToId = (hash) => {const el = document.querySelector(hash);
    if (!el) return;
    if (lenisRef.current) {
      // recalc dimensions first — after an SPA route change the document height
      // has changed, so lenis would otherwise scroll to a stale offset.
      lenisRef.current.resize();
      lenisRef.current.scrollTo(el, { offset: -80 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest("a[data-scroll]");
      if (!a) return;
      const hash = a.getAttribute("data-scroll");
      e.preventDefault();
      if (location.pathname !== "/") {
        pendingHash.current = hash;
        navigate("/");
      } else {
        scrollToId(hash);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [location.pathname, navigate]);

  // scroll handling on route change
  useEffect(() => {
    // track pageviews (skips prerender bots + the admin dashboard itself)
    if (location.pathname !== "/admin") {
      trackPageview(location.pathname);
    }
    if (pendingHash.current && location.pathname === "/") {
      const hash = pendingHash.current;
      pendingHash.current = null;
      // retry as images load and the layout settles, so we land on the section, not short of it
      [120, 450, 900, 1500].forEach((t) => setTimeout(() => scrollToId(hash), t));
    } else if (location.hash) {
      // deep link to a section on any page (e.g. /transport#biking)
      [120, 450, 900, 1500].forEach((t) => setTimeout(() => scrollToId(location.hash), t));
    } else {
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash]);

  return (
    <div className="App grain">
      <Nav />
      {children}
      <Footer />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/planner" element={<PlannerPage />} />
            <Route path="/plan/:code" element={<PlanPage />} />
            <Route path="/areas" element={<AreasPage />} />
            <Route path="/areas/:id" element={<AreaDetail />} />
            <Route path="/trips" element={<TripsPage />} />
            <Route path="/beyond-copenhagen" element={<BeyondCopenhagenPage />} />
            <Route path="/trips/:id" element={<TripDetail />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/seasons" element={<Navigate to="/copenhagen-in" replace />} />
            <Route path="/copenhagen-in" element={<Navigate to={`/copenhagen-in/${MONTH_SLUGS[new Date().getMonth()]}`} replace />} />
            <Route path="/copenhagen-in/:month" element={<MonthPage />} />
            <Route path="/stay" element={<AccommodationPage />} />
            <Route path="/bakeries" element={<BakeriesPage />} />
            <Route path="/transport" element={<TransportPage />} />
            <Route path="/biking" element={<BikingPage />} />
            <Route path="/christmas" element={<ChristmasPage />} />
            <Route path="/christmas-walk" element={<ChristmasWalkPage />} />
            <Route path="/bakery-crawl" element={<BakeryCrawlPage />} />
            <Route path="/christmas-food" element={<ChristmasFoodPage />} />
            <Route path="/glogg" element={<GloggPage />} />
            <Route path="/budget" element={<BudgetPage />} />
            <Route path="/rainy-day" element={<RainyDayPage />} />
            <Route path="/layover" element={<LayoverPage />} />
            <Route path="/harbour-swimming" element={<HarbourSwimmingPage />} />
            <Route path="/smorrebrod" element={<SmorrebrodPage />} />
            <Route path="/danish-food" element={<DanishFoodPage />} />
            <Route path="/hc-andersen" element={<HcAndersenPage />} />
            <Route path="/photo-worthy-streets" element={<PhotoStreetsPage />} />
            <Route path="/copenhagen-shopping-streets" element={<ShoppingStreetsPage />} />
            <Route path="/copenhagen-food-markets" element={<FoodMarketsPage />} />
            <Route path="/things-to-do" element={<ThingsToDoPage />} />
          <Route path="/copenhagen-with-kids" element={<KidsPage />} />
          <Route path="/copenhagen-like-a-local" element={<LikeALocalPage />} />
          <Route path="/copenhagen-with-kids-itinerary" element={<KidsItineraryPage />} />
          <Route path="/copenhagen-in-winter" element={<WinterPage />} />
            <Route path="/copenhagen-itinerary" element={<ItineraryPage />} />
            <Route path="/edt" element={<EdtPage />} />
            <Route path="/two-days-in-copenhagen" element={<Navigate to="/copenhagen-itinerary" replace />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/advertise/success" element={<AdvertiseSuccess />} />
            <Route path="/advertise/cancel" element={<AdvertiseCancel />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
